import React, {useEffect, useMemo, useRef, useState} from "react";
import {
  APageLoader,
  APanel,
  APanelBody,
  useATheme
} from "@cisco-sbg-ui/magna-react";
import {t} from "i18next";
import {Chart, FontLoader} from "regraph";
import {useSelector} from "react-redux";
import EmptyContent from "components/EmptyContent";
import {
  selectAttackGraphGroups,
  selectAttackGraphInitialized,
  selectAttackGraphItems,
  selectHasAttackGraphItems,
  selectIncidentsOverviewLoading
} from "store/selectors/incidentOverviewSelectors";
import {
  DEFAULT_ATTACK_GRAPH_LAYOUT,
  DEFAULT_COMBINE_ON,
  GRAPH_THEME_OPTIONS,
  PING_OPTIONS
} from "utils/graph/graphStyleConstants";
import {
  getComboStyle,
  handleItemInteraction,
  setNodeStyle
} from "utils/graph/graphStyleUtils";
import {getAttackGraphOptions} from "utils/graph/graphUtils";
import FullScreenButton from "./AttackGraphTools/FullScreenButton";
import OrientationSelectDropdown from "./AttackGraphTools/OrientationSelectDropdown";
import styles from "./AttackGraph.module.scss";

const AttackGraph = () => {
  const chartRef = useRef();

  const {currentTheme, isLight} = useATheme();

  const attackGraphItems = useSelector(selectAttackGraphItems);
  const attackGraphGroups = useSelector(selectAttackGraphGroups);
  const isLoading = useSelector(selectIncidentsOverviewLoading);
  const attackGraphInitialized = useSelector(selectAttackGraphInitialized);
  const hasAttackGraphItems = useSelector(selectHasAttackGraphItems);

  const [graphViewModel, setGraphViewModel] = useState({});
  const [combine, setCombine] = useState(DEFAULT_COMBINE_ON);
  const [openCombos, setOpenCombos] = useState({});
  const [zoom, setZoom] = useState(0.1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [layout, setLayout] = useState(DEFAULT_ATTACK_GRAPH_LAYOUT);

  const graphOptions = useMemo(
    () =>
      getAttackGraphOptions(
        isLight ? GRAPH_THEME_OPTIONS.LIGHT : GRAPH_THEME_OPTIONS.DARK
      ),
    [isLight]
  );

  useEffect(() => {
    setGraphViewModel(setNodeStyle(attackGraphItems, currentTheme));
  }, [attackGraphItems, currentTheme]);

  // Re-run styling on Combos when theme changes
  useEffect(() => {
    setCombine({...combine});
  }, [currentTheme]);

  // Re-fit to view when isFullscreen changes
  useEffect(() => {
    handleFit("all");
  }, [isFullscreen]);

  const handleNodeClick = ({id}) => {
    if (chartRef.current && id) {
      chartRef.current.ping({[id]: true}, PING_OPTIONS);
    }
  };

  const handleDoubleClick = ({id}) => {
    setOpenCombos({...openCombos, [id]: !openCombos[id]});
    setCombine({...combine});
  };

  const handleCombineNodes = ({setStyle, id, nodes, combo}) => {
    setStyle(
      getComboStyle(
        id,
        nodes,
        combo,
        attackGraphGroups,
        openCombos,
        currentTheme
      )
    );
  };

  const handleSetOrientation = (o) => {
    setLayout({...layout, orientation: o});
  };

  const handleFit = (fitType) => {
    if (chartRef.current) {
      chartRef.current.fit(fitType);
    }
  };

  return (
    <APanel
      className={
        !isFullscreen ? styles.graphPanel : styles.graphPanelFullscreen
      }>
      <APanelBody
        className={
          !isFullscreen
            ? styles.graphContainer
            : styles.graphContainerFullscreen
        }>
        {isLoading && (
          <div className={styles.graphLoader}>
            <APageLoader className={styles.graphLoaderSpinner} />
          </div>
        )}

        {attackGraphInitialized && !isLoading && !hasAttackGraphItems && (
          <EmptyContent
            className={styles.emptyState}
            name={t("overview.graph.emptyName")}
          />
        )}

        <Chart
          combine={combine}
          items={graphViewModel}
          layout={layout}
          options={{...graphOptions}}
          ref={chartRef}
          onClick={handleNodeClick}
          onDoubleClick={handleDoubleClick}
          onCombineNodes={handleCombineNodes}
          onItemInteraction={handleItemInteraction}
        />
        <OrientationSelectDropdown setOrientation={handleSetOrientation} />
        <FullScreenButton
          isFullscreen={isFullscreen}
          setIsFullscreen={setIsFullscreen}
        />
      </APanelBody>
    </APanel>
  );
};

export default AttackGraph;
